import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';
import { useProfile } from '../../../hooks';
import { createUserProfile } from '../../../lib/api';
import { getStoredLocale } from '../../../i18n';
import { getDefaultAuthenticatedPath } from '../../../lib/utils/subscriptionAccess';

const RegisterForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signUp, user } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Redirect if already logged in
  useEffect(() => {
    if (user && !profileLoading) {
      navigate(getDefaultAuthenticatedPath(profile), { replace: true });
    }
  }, [user, profile, profileLoading, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = t('auth.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = t('auth.emailInvalid');
    }

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = t('auth.firstNameRequired');
    } else if (formData?.firstName?.trim()?.length < 2) {
      newErrors.firstName = t('auth.firstNameMinLength', { count: 2 });
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = t('auth.lastNameRequired');
    } else if (formData?.lastName?.trim()?.length < 2) {
      newErrors.lastName = t('auth.lastNameMinLength', { count: 2 });
    }

    if (formData?.company?.trim() && formData.company.trim().length < 2) {
      newErrors.company = t('auth.companyMinLength', { count: 2 });
    }

    if (!formData?.password) {
      newErrors.password = t('auth.passwordRequired');
    } else if (formData?.password?.length < 8) {
      newErrors.password = t('auth.passwordMinLength', { count: 8 });
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/?.test(formData?.password)) {
      newErrors.password = t('auth.passwordComplexity');
    }

    if (!formData?.confirmPassword) {
      newErrors.confirmPassword = t('auth.confirmPasswordRequired');
    } else if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = t('auth.passwordsDoNotMatch');
    }

    if (!formData?.agreeTerms) {
      newErrors.agreeTerms = t('auth.agreeTermsRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      // Step 1: Create auth user
      const preferredLocale = getStoredLocale();
      const { data, error } = await signUp(
        formData?.email,
        formData?.password,
        {
          first_name: formData?.firstName,
          last_name: formData?.lastName,
          company: formData?.company,
          full_name: `${formData?.firstName} ${formData?.lastName}`,
          preferred_locale: preferredLocale,
        }
      );

      if (error) {
        if (error.message?.includes('already registered')) {
          setErrors({
            email: t('auth.emailAlreadyRegistered')
          });
        } else {
          setErrors({
            email: error.message || t('auth.registrationFailed')
          });
        }
        return;
      }

      // Step 2: Profile row is created by DB trigger on auth.users insert.
      // Only enrich it from the client when we have an authenticated session (RLS).
      if (data?.user && data?.session) {
        try {
          await createUserProfile(data.user.id, {
            full_name: `${formData.firstName} ${formData.lastName}`,
            preferred_locale: preferredLocale,
            ...(formData.company?.trim() && { company_name: formData.company.trim() }),
          });
        } catch (profileError) {
          console.error('Error updating user profile after signup:', profileError);
        }
      }

      // Step 3: Handle post-registration flow
      if (data?.user && !data?.session) {
        // Email confirmation required
        setSuccessMessage(t('auth.registrationSuccessEmail'));
      } else {
        // Auto-login successful — Demo is required before billing/dashboard.
        setSuccessMessage(t('auth.registrationSuccessRedirect'));
        setTimeout(() => {
          navigate('/demo', { replace: true });
        }, 1500);
      }

    } catch (error) {
      console.error('Registration error:', error);
      setErrors({
        email: t('common.unexpectedError')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {successMessage && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">{successMessage}</p>
        </div>
      )}
      
      <Input
        label={t('auth.email')}
        type="email"
        name="email"
        placeholder={t('auth.emailPlaceholder')}
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        required
        disabled={loading}
        dense
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label={t('auth.firstName')}
          type="text"
          name="firstName"
          placeholder={t('auth.firstNamePlaceholder')}
          value={formData?.firstName}
          onChange={handleChange}
          error={errors?.firstName}
          required
          disabled={loading}
          dense
        />

        <Input
          label={t('auth.lastName')}
          type="text"
          name="lastName"
          placeholder={t('auth.lastNamePlaceholder')}
          value={formData?.lastName}
          onChange={handleChange}
          error={errors?.lastName}
          required
          disabled={loading}
          dense
        />
      </div>
      
      <Input
        label={t('auth.companyOptional')}
        type="text"
        name="company"
        placeholder={t('auth.companyPlaceholder')}
        value={formData?.company}
        onChange={handleChange}
        error={errors?.company}
        disabled={loading}
        dense
      />
      
      <Input
        label={t('auth.password')}
        type="password"
        name="password"
        placeholder={t('auth.createPasswordPlaceholder')}
        description={t('auth.passwordRequirements')}
        descriptionClassName="text-[11px] leading-snug"
        value={formData?.password}
        onChange={handleChange}
        error={errors?.password}
        required
        disabled={loading}
        dense
      />
      
      <Input
        label={t('auth.confirmPassword')}
        type="password"
        name="confirmPassword"
        placeholder={t('auth.confirmPasswordPlaceholder')}
        value={formData?.confirmPassword}
        onChange={handleChange}
        error={errors?.confirmPassword}
        required
        disabled={loading}
        dense
      />
      
      <Checkbox
        label={t('auth.agreeTerms')}
        name="agreeTerms"
        checked={formData?.agreeTerms}
        onChange={handleChange}
        error={errors?.agreeTerms}
        required
        disabled={loading}
      />
      
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={loading}
        className="mt-4"
      >
        {t('auth.createAccount')}
      </Button>
    </form>
  );
};

export default RegisterForm;
